import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { useGuidance } from "@/lib/GuidanceContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { 
  Check, 
  X, 
  Clock, 
  Calendar,
  ArrowLeft,
  RefreshCw,
  Loader2,
  Facebook,
  Instagram,
  Sparkles,
  Heart,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Play,
  Volume2,
  VolumeX
} from "lucide-react";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const platformIcons = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: TikTokIcon
};

const platformColors = {
  facebook: "#1877F2",
  instagram: "#E4405F",
  tiktok: "#000000"
};

// Tinder-like Swipe Card Component
function SwipeCard({ post, onSwipe, isTop, onUndo }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-25, 0, 25]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  
  // Overlay opacity based on swipe direction
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);
  
  const [exitDirection, setExitDirection] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const PlatformIcon = platformIcons[post.platform] || Facebook;

  const handleDragEnd = (event, info) => {
    const threshold = 100;
    const velocity = info.velocity.x;
    const offset = info.offset.x;
    
    // Determine swipe based on offset or velocity
    if (offset > threshold || velocity > 500) {
      setExitDirection("right");
      onSwipe(true, post);
    } else if (offset < -threshold || velocity < -500) {
      setExitDirection("left");
      onSwipe(false, post);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not scheduled";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      hour: "numeric", 
      minute: "2-digit" 
    });
  };

  return (
    <motion.div
      className="absolute w-full h-full"
      style={{ x, rotate, opacity }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.9}
      onDragEnd={handleDragEnd}
      initial={{ scale: isTop ? 1 : 0.95, y: isTop ? 0 : 10 }}
      animate={{ 
        scale: isTop ? 1 : 0.95,
        y: isTop ? 0 : 10,
        x: exitDirection === "right" ? 500 : exitDirection === "left" ? -500 : 0
      }}
      exit={{
        x: exitDirection === "right" ? 500 : -500,
        opacity: 0,
        transition: { duration: 0.3 }
      }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <div className={`card-base overflow-hidden h-full ${isTop ? 'cursor-grab active:cursor-grabbing' : ''} select-none`}>
        {/* LIKE Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/30 to-green-500/50 z-20 pointer-events-none flex items-center justify-center"
          style={{ opacity: likeOpacity }}
        >
          <div className="border-4 border-green-500 text-green-500 px-8 py-4 rounded-lg font-bold text-4xl rotate-[-20deg]">
            APPROVE
          </div>
        </motion.div>
        
        {/* NOPE Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-l from-transparent via-red-500/30 to-red-500/50 z-20 pointer-events-none flex items-center justify-center"
          style={{ opacity: nopeOpacity }}
        >
          <div className="border-4 border-red-500 text-red-500 px-8 py-4 rounded-lg font-bold text-4xl rotate-[20deg]">
            REJECT
          </div>
        </motion.div>

        {/* Platform badge */}
        <div 
          className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm"
          style={{ backgroundColor: `${platformColors[post.platform]}90` }}
        >
          <PlatformIcon className="w-4 h-4 text-white" />
          <span className="text-xs font-bold text-white capitalize">{post.platform}</span>
        </div>

        {/* Viral score */}
        <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
          <Sparkles className="w-4 h-4 text-viraly-orange" />
          <span className="text-sm font-bold text-viraly-orange">
            {post.viral_score || 75}%
          </span>
        </div>

        {/* Media - Video or Image */}
        <div className="h-[55%] bg-viraly-dark relative overflow-hidden">
          {post.video_url ? (
            <>
              <video 
                ref={videoRef}
                src={post.video_url} 
                className="w-full h-full object-cover"
                loop
                muted={isMuted}
                playsInline
                autoPlay
                poster={post.image_url}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted(!isMuted);
                }}
                className="absolute bottom-4 right-4 z-10 p-2 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
              >
                {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
              </button>
              <div className="absolute bottom-4 left-4 z-10 flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-500/80 backdrop-blur-sm">
                <Play className="w-3 h-3 text-white" fill="white" />
                <span className="text-xs font-medium text-white">Video</span>
              </div>
            </>
          ) : post.image_url ? (
            <img 
              src={post.image_url} 
              alt="Post" 
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-viraly-card to-viraly-dark">
              <div className="text-6xl opacity-30">📝</div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 h-[45%] overflow-hidden flex flex-col">
          {/* Title if exists */}
          {post.title && (
            <h3 className="font-bold text-lg text-viraly-orange mb-2 line-clamp-1">{post.title}</h3>
          )}
          
          {/* Caption */}
          <p className="text-sm text-viraly-light mb-3 line-clamp-3 flex-1">
            {post.caption}
          </p>

          {/* Hashtags */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.hashtags.slice(0, 4).map((tag, i) => (
                <span 
                  key={i}
                  className="px-2 py-0.5 rounded-full bg-viraly-orange/20 text-xs text-viraly-orange"
                >
                  #{tag.replace('#', '')}
                </span>
              ))}
              {post.hashtags.length > 4 && (
                <span className="text-xs text-viraly-muted">+{post.hashtags.length - 4}</span>
              )}
            </div>
          )}

          {/* Schedule info */}
          <div className="flex items-center gap-2 text-xs text-viraly-muted border-t border-white/10 pt-3 mt-auto">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(post.scheduled_at)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function SwipeApprovePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { markPostApproved } = useGuidance();
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ approved: 0, rejected: 0, total: 0 });
  const [history, setHistory] = useState([]); // For undo functionality
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile/PWA
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch scheduled posts
  useEffect(() => {
    fetchScheduledPosts();
  }, []);

  const fetchScheduledPosts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/posts");
      // Filter for scheduled posts only
      const scheduledPosts = (response.data || []).filter(p => 
        p.status === "scheduled" || p.status === "draft"
      );
      setPosts(scheduledPosts);
      setStats({ approved: 0, rejected: 0, total: scheduledPosts.length });
    } catch (error) {
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (approved, post) => {
    // Save to history for undo
    setHistory(prev => [...prev, { post, approved, index: currentIndex }]);
    
    try {
      if (approved) {
        setStats(prev => ({ ...prev, approved: prev.approved + 1 }));
        toast.success("Post approved!", { 
          duration: 1500,
          icon: "✓"
        });
        // Mark post as approved for UI guidance glow
        if (markPostApproved) markPostApproved();
      } else {
        await apiClient.delete(`/posts/${post.id}`);
        setStats(prev => ({ ...prev, rejected: prev.rejected + 1 }));
        toast("Post removed", { 
          duration: 1500,
          icon: "✗"
        });
      }
    } catch (error) {
      console.error("Error handling swipe:", error);
    }

    // Move to next
    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, 100);
  };

  const handleUndo = async () => {
    if (history.length === 0) return;
    
    const lastAction = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    
    // If we rejected, we need to restore the post (it was deleted)
    if (!lastAction.approved) {
      // Can't really restore a deleted post easily, but we can refetch
      toast.info("Undo: Refreshing posts...");
      await fetchScheduledPosts();
    } else {
      setCurrentIndex(prev => prev - 1);
      setStats(prev => ({ ...prev, approved: prev.approved - 1 }));
    }
    
    toast.success("Undone!", { duration: 1000 });
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex >= posts.length) return;
      
      if (e.key === "ArrowRight" || e.key === "l") {
        handleSwipe(true, posts[currentIndex]);
      } else if (e.key === "ArrowLeft" || e.key === "h") {
        handleSwipe(false, posts[currentIndex]);
      } else if (e.key === "z" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleUndo();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, posts, history]);

  const resetAndStartOver = () => {
    setCurrentIndex(0);
    setHistory([]);
    setStats({ approved: 0, rejected: 0, total: posts.length });
    fetchScheduledPosts();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[70vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-viraly-orange mx-auto mb-4" />
            <p className="text-viraly-muted">Loading your posts...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isComplete = currentIndex >= posts.length;
  const remainingPosts = posts.slice(currentIndex, currentIndex + 3); // Show up to 3 cards in stack

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-lg mx-auto" data-testid="swipe-approve-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate("/posts")} 
            className="flex items-center gap-2 text-viraly-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" /> 
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="text-center">
            <h1 className="font-heading text-xl md:text-2xl font-bold gradient-text">Swipe to Approve</h1>
            <p className="text-xs text-viraly-muted mt-1">
              {posts.length - currentIndex} posts remaining
            </p>
          </div>
          <button 
            onClick={resetAndStartOver}
            className="p-2 rounded-xl hover:bg-white/10 transition-colors text-viraly-muted hover:text-white"
            title="Start over"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-viraly-gradient"
              initial={{ width: 0 }}
              animate={{ width: `${(currentIndex / posts.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-viraly-muted">
            <span>{currentIndex} reviewed</span>
            <span>{posts.length} total</span>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center justify-center gap-8 mb-6 p-3 rounded-xl bg-white/5">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
            <p className="text-xs text-viraly-muted">Approved</p>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
            <p className="text-xs text-viraly-muted">Rejected</p>
          </div>
          <div className="w-px h-10 bg-white/20"></div>
          <div className="text-center">
            <p className="text-2xl font-bold text-viraly-orange">{posts.length - currentIndex}</p>
            <p className="text-xs text-viraly-muted">Left</p>
          </div>
        </div>

        {/* Card Stack */}
        {isComplete ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-400 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold mb-2">All Done!</h2>
            <p className="text-viraly-muted mb-2">
              You reviewed <span className="text-white font-bold">{stats.total}</span> posts
            </p>
            <p className="text-sm text-viraly-muted mb-8">
              <span className="text-green-500">{stats.approved} approved</span>
              {" • "}
              <span className="text-red-500">{stats.rejected} rejected</span>
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/posts")} className="btn-primary px-8">
                View All Posts
              </Button>
              <Button onClick={resetAndStartOver} variant="outline" className="border-white/20">
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Swipe Cards Stack */}
            <div className="relative h-[420px] md:h-[480px] mb-6 touch-none">
              <AnimatePresence mode="popLayout">
                {remainingPosts.map((post, index) => (
                  <SwipeCard
                    key={post.id}
                    post={post}
                    isTop={index === 0}
                    onSwipe={handleSwipe}
                    onUndo={handleUndo}
                  />
                )).reverse()}
              </AnimatePresence>
              
              {/* Empty state when no posts */}
              {remainingPosts.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-viraly-muted">
                  <p>No more posts to review</p>
                </div>
              )}
            </div>

            {/* Action Buttons - Tinder Style */}
            <div className="flex items-center justify-center gap-4 md:gap-6">
              {/* Undo Button */}
              <button
                onClick={handleUndo}
                disabled={history.length === 0}
                className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${
                  history.length > 0 
                    ? 'bg-yellow-500/20 border-2 border-yellow-500 hover:bg-yellow-500 hover:text-white group' 
                    : 'bg-white/5 border-2 border-white/20 cursor-not-allowed opacity-50'
                }`}
                title="Undo (Ctrl+Z)"
                data-testid="swipe-undo-btn"
              >
                <RotateCcw className={`w-5 h-5 md:w-6 md:h-6 ${history.length > 0 ? 'text-yellow-500 group-hover:text-white' : 'text-white/30'}`} />
              </button>

              {/* Reject Button */}
              <button
                onClick={() => posts[currentIndex] && handleSwipe(false, posts[currentIndex])}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-red-500/20 border-[3px] border-red-500 flex items-center justify-center hover:bg-red-500 hover:scale-110 transition-all group shadow-lg shadow-red-500/20"
                data-testid="swipe-reject-btn"
              >
                <X className="w-8 h-8 md:w-10 md:h-10 text-red-500 group-hover:text-white" strokeWidth={3} />
              </button>
              
              {/* Approve Button */}
              <button
                onClick={() => posts[currentIndex] && handleSwipe(true, posts[currentIndex])}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-green-500/20 border-[3px] border-green-500 flex items-center justify-center hover:bg-green-500 hover:scale-110 transition-all group shadow-lg shadow-green-500/20"
                data-testid="swipe-approve-btn"
              >
                <Heart className="w-10 h-10 md:w-12 md:h-12 text-green-500 group-hover:text-white" fill="currentColor" />
              </button>

              {/* Desktop: Skip Button */}
              {!isMobile && (
                <button
                  onClick={() => {
                    setCurrentIndex(prev => prev + 1);
                    toast("Skipped", { duration: 1000 });
                  }}
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center hover:bg-blue-500 hover:text-white group transition-all shadow-lg"
                  title="Skip for now"
                  data-testid="swipe-skip-btn"
                >
                  <ChevronRight className="w-6 h-6 text-blue-500 group-hover:text-white" />
                </button>
              )}
            </div>

            {/* Keyboard hints - Desktop only */}
            {!isMobile && (
              <div className="mt-6 text-center">
                <p className="text-xs text-viraly-muted">
                  <kbd className="px-2 py-1 rounded bg-white/10 mx-1 font-mono">←</kbd> Reject
                  <span className="mx-2">•</span>
                  <kbd className="px-2 py-1 rounded bg-white/10 mx-1 font-mono">→</kbd> Approve
                  <span className="mx-2">•</span>
                  <kbd className="px-2 py-1 rounded bg-white/10 mx-1 font-mono">Ctrl+Z</kbd> Undo
                </p>
              </div>
            )}

            {/* Swipe hint - Mobile only */}
            {isMobile && currentIndex === 0 && (
              <motion.p 
                className="text-center text-xs text-viraly-muted mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                Swipe right to approve, left to reject
              </motion.p>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
